type RefreshTokenFn = () => Promise<string | null>;

/**
 * Falha HTTP de uma chamada de API. `message` ja e a mensagem do backend quando
 * ele mandou uma (ver apiMessageOf), entao uma tela pode mostrar `error.message`
 * direto ao usuario; `body` e `status` ficam para quem precisa do detalhe cru.
 */
export class GatewayHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    message?: string,
  ) {
    super(message ?? `Gateway request failed (${status}): ${body}`);
    this.name = 'GatewayHttpError';
  }
}

let accessToken: string | null = null;
let activePartnerId: string | null = null;
let refreshTokenFn: RefreshTokenFn = async () => null;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null = null, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) promise.reject(error);
    else promise.resolve(token as string);
  });
  failedQueue = [];
};

export function setGatewayAccessToken(token: string | null) {
  accessToken = token;
}

/**
 * The account (partner) this tab is showing. Switching accounts keeps the token, so an API that
 * caches identity per token (the `whoami` mode of preview and local dev) would keep answering for
 * the previous account; this header tells it to ask the gateway again. It is never identity: the
 * partner that counts is always the one the gateway resolves, and behind the gateway it is ignored.
 */
export function setActivePartnerId(partnerId: string | null) {
  activePartnerId = partnerId;
}

export function setGatewayRefreshTokenFn(fn: RefreshTokenFn) {
  refreshTokenFn = fn;
}

export type GatewayRequestInit = Omit<RequestInit, 'headers'> & {
  headers?: HeadersInit;
  query?: Record<string, string | number | boolean | null | undefined>;
  /**
   * Base desta chamada. Ausente = o gateway da uMode (o caminho normal).
   * Uma base RELATIVA ('/api') fala com o backend que roda ao lado — e o que o
   * preview do Brainwave usa para exercitar a API da propria sessao.
   */
  baseUrl?: string;
  __refreshToken?: boolean;
};

function buildURL(path: string, query?: GatewayRequestInit['query'], baseUrl?: string) {
  const base = baseUrl ?? process.env.NEXT_PUBLIC_BASE_API_GATEWAY_URL;

  if (!base) {
    throw new Error('NEXT_PUBLIC_BASE_API_GATEWAY_URL is not defined');
  }

  const normalizedPath = path.replace(/^\//, '');

  // Base relativa (mesma origem): `new URL` exige base absoluta, entao monta a
  // string direto — tambem funciona no SSR, onde nao ha origem.
  if (base.startsWith('/')) {
    const search = new URLSearchParams();
    if (query) {
      for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null) continue;
        search.set(key, String(value));
      }
    }
    const qs = search.toString();
    return `${base.replace(/\/$/, '')}/${normalizedPath}${qs === '' ? '' : `?${qs}`}`;
  }

  const url = new URL(normalizedPath, base.endsWith('/') ? base : `${base}/`);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url;
}

function mergeHeaders(base: HeadersInit | undefined, extra: HeadersInit | undefined) {
  const headers = new Headers(base);
  if (extra) {
    const extraHeaders = new Headers(extra);
    extraHeaders.forEach((value, key) => headers.set(key, value));
  }
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return (await res.json()) as T;
  }
  return (await res.text()) as T;
}

/** Texto legivel de um campo que pode vir string, lista (validacao do DTO) ou outra coisa. */
function readableText(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const parts = value.filter((part): part is string => typeof part === 'string' && part !== '');
    return parts.length === 0 ? undefined : parts.join(', ');
  }
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/**
 * Mensagem de usuario que veio do backend. O envelope de erro da casa e
 * `{ success: false, error: { message } }`; o Nest cru responde `{ message }`; e
 * o proxy do preview responde `{ error: '<texto>' }`.
 * Sem isto, `error.message` numa tela mostraria o JSON inteiro, e quem usa o
 * app leria "Gateway request failed (409): {...}" em vez do que aconteceu.
 */
function apiMessageOf(body: string): string | undefined {
  if (body === '') {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(body);
    if (parsed === null || typeof parsed !== 'object') {
      return undefined;
    }
    const envelope = parsed as { message?: unknown; error?: unknown };
    const nested =
      envelope.error !== null && typeof envelope.error === 'object'
        ? (envelope.error as { message?: unknown }).message
        : undefined;
    // `error` como string fica por ultimo: no Nest cru ele e so o nome do status
    // ('Unauthorized'), enquanto `message` e o que a pessoa precisa ler.
    return readableText(nested) ?? readableText(envelope.message) ?? readableText(envelope.error);
  } catch {
    return undefined;
  }
}

async function settle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new GatewayHttpError(res.status, body, apiMessageOf(body));
  }
  return parseResponse<T>(res);
}

export async function gatewayFetch<T>(path: string, init: GatewayRequestInit = {}): Promise<T> {
  const { query, headers: initHeaders, baseUrl, __refreshToken = true, ...rest } = init;

  const headers = mergeHeaders(
    {
      Accept: 'application/json, text/plain, */*',
    },
    initHeaders,
  );

  if (accessToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  if (activePartnerId !== null && activePartnerId !== '') {
    headers.set('x-active-partner-id', activePartnerId);
  }

  const url = buildURL(path, query, baseUrl);
  const res = await fetch(url, { ...rest, headers });

  if (res.status !== 401 || !__refreshToken) {
    return await settle<T>(res);
  }

  if ((rest as { __retried?: boolean }).__retried) {
    const body = await res.text().catch(() => '');
    throw new GatewayHttpError(res.status, body, `Unauthorized (${res.status}): ${body}`);
  }

  if (isRefreshing) {
    const token = await new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });

    const retryHeaders = mergeHeaders(headers, undefined);
    retryHeaders.set('Authorization', `Bearer ${token}`);

    const retryRes = await fetch(url, { ...rest, headers: retryHeaders, __retried: true } as RequestInit);
    return await settle<T>(retryRes);
  }

  isRefreshing = true;
  try {
    const newAccessToken = await refreshTokenFn();
    if (!newAccessToken) {
      const err = new Error('Refresh token not configured');
      processQueue(err, null);
      throw err;
    }

    accessToken = newAccessToken;
    processQueue(null, newAccessToken);

    const retryHeaders = mergeHeaders(headers, undefined);
    retryHeaders.set('Authorization', `Bearer ${newAccessToken}`);

    const retryRes = await fetch(url, { ...rest, headers: retryHeaders, __retried: true } as RequestInit);
    return await settle<T>(retryRes);
  } catch (e) {
    const err = e instanceof Error ? e : new Error('Unknown refresh error');
    processQueue(err, null);
    throw e;
  } finally {
    isRefreshing = false;
  }
}
